import React, { useEffect, useMemo, useState, useRef } from 'react';
import { observer } from 'mobx-react';
import { Field, Formik, FormikProps } from 'formik';
import _ from 'lodash';
import { AddressType, CountryType, RelationshipType, SexType, StateType, SupportPayer, SupportSubscriber, CreateClaimSubscriber, ProviderPayer } from '../../../../api';
import { getOptions } from '../../../base/select';
import * as S from '../form.styles';
import { ClaimEditorStore, DISABLED_PAYER_MESSAGE } from '../../../../stores/claim-editor';
import { InputField, SelectField } from '../../../base/form';
import { AsyncSelectField } from '../../../base/form/async-select/async-select';
import { DatePickerField } from '../../../base/form/datepicker/date-picker';
import { validateSubscriberFormValues } from '../../../../stores/claim-editor/claim-editor-rules';
import { fieldDomId } from '../../../../stores/claim-editor/claim-editor-sections';
import { formatLookupInfo } from '../../../../stores/claim-editor/claim-editor-lookup-info';
import { useClaimEditorRevealFieldErrors } from '../use-claim-editor-form-validation';
import { searchPayers } from '../../../../services/payer-search/payer-search';

interface SubscriberPayerFormValues {
    subscriberLookup?: string;
    subscriberId?: string;
    payerLookup?: string;
    payerId?: string;
    memberId?: string;
    ssn?: string;
    first: string;
    middle?: string;
    last: string;
    suffix?: string;
    dob?: string;
    sex?: SexType | '';
    phone?: string;
    relationshipToPatient?: RelationshipType | '';
    address1: string;
    address2?: string;
    city: string;
    state?: StateType | '';
    country?: CountryType | '';
    postal: string;
}

/**
 * How many payers the lookup will render at once.
 *
 * 50, matching `billing-provider-form` in this same editor — the payer lookup was
 * the outlier at 10 while its siblings used 20 and 50, on the field with by far
 * the largest list. react-select scrolls its own menu at this size, which is what
 * makes the results past the first handful reachable at all.
 */
const MAX_PAYER_OPTIONS = 50;

const emptyValues: SubscriberPayerFormValues = {
    subscriberLookup: '',
    subscriberId: '',
    payerLookup: '',
    payerId: '',
    memberId: '',
    ssn: '',
    first: '',
    middle: '',
    last: '',
    suffix: '',
    dob: '',
    sex: '' as any,
    phone: '',
    relationshipToPatient: '' as any,
    address1: '',
    address2: '',
    city: '',
    state: '' as any,
    country: CountryType.US as any,
    postal: '',
};

interface ISubscriberPayerFormProps {
    claimEditorStore: ClaimEditorStore;
    data?: CreateClaimSubscriber;
    subscriberIndex: number;
}

export const SubscriberPayerForm: React.FunctionComponent<ISubscriberPayerFormProps> = observer(({ claimEditorStore, data, subscriberIndex }) => {
    const validationSignal = claimEditorStore.validationSignal;
    const validationEvaluated = claimEditorStore.validationEvaluated;
    const sexOptions = getOptions(SexType);
    const stateOptions = getOptions(StateType);
    const countryOptions = getOptions(CountryType);
    const relationshipOptions = getOptions(RelationshipType);
    const formikRef = useRef<FormikProps<SubscriberPayerFormValues> | null>(null);
    const fieldPrefix = `subscribers[${subscriberIndex}]`;

    const convertToSubscriber = (v: SubscriberPayerFormValues): CreateClaimSubscriber => {
        const lines = [v.address1].filter(Boolean);
        if (v.address2) lines.push(v.address2);
        return {
            subscriberId: v.subscriberId || undefined,
            first: v.first || '',
            middle: v.middle || undefined as any,
            last: v.last || '',
            suffix: v.suffix?.trim() ? v.suffix.trim() : undefined as any,
            dob: (v.dob as any) || undefined,
            sex: (v.sex as any) || undefined,
            phone: v.phone || undefined,
            relationshipToPatient: (v.relationshipToPatient as any) || undefined,
            memberId: v.memberId || '',
            ssn: v.ssn || undefined,
            payerId: v.payerId || '',
            addresses: [
                {
                    name: `${(v.first || '').trim()} ${(v.last || '').trim()}`.trim(),
                    lines,
                    city: v.city || '',
                    state: (v.state as any) || undefined,
                    postal: v.postal || '',
                    country: (v.country as any) || CountryType.US,
                    type: AddressType.MAILING,
                } as any,
            ],
        } as CreateClaimSubscriber;
    };

    const persistSubscriber = (values: SubscriberPayerFormValues): void => {
        const updatedSubscriber = convertToSubscriber(values);
        const subscribers = [...(claimEditorStore.subscribers || [])];
        subscribers[subscriberIndex] = updatedSubscriber;
        claimEditorStore.updateSubscribers(subscribers);
    };

    const debouncedSave = useMemo(
        () =>
            _.debounce(() => {
                const latestValues = formikRef.current?.values;
                if (!latestValues) return;
                persistSubscriber(latestValues);
            }, 500),
        [claimEditorStore, subscriberIndex],
    );

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
        };
    }, [debouncedSave]);

    const initialValues = useMemo<SubscriberPayerFormValues>(() => {
        const s = data;
        if (!s) return emptyValues;
        const addr = s.addresses?.[0];
        return {
            subscriberLookup: s.last || '',
            subscriberId: (s as any).subscriberId || (s as any).id || '',
            payerLookup: s.payerId || '',
            payerId: s.payerId || '',
            memberId: s.memberId || '',
            ssn: s.ssn || '',
            first: s.first || '',
            middle: s.middle || '',
            last: s.last || '',
            suffix: s.suffix || '',
            dob: (s.dob as any) || '',
            sex: (s.sex as any) || '' as any,
            phone: s.phone || '',
            relationshipToPatient: (s as any).relationshipToPatient || '' as any,
            address1: addr?.lines?.[0] || '',
            address2: addr?.lines?.[1] || '',
            city: addr?.city || '',
            state: (addr?.state as any) || '' as any,
            country: (addr?.country as any) || CountryType.US as any,
            postal: addr?.postal || '',
        };
    }, [data]);

    return (
        <Formik<SubscriberPayerFormValues>
            innerRef={formikRef as any}
            initialValues={initialValues}
            validate={values =>
                validateSubscriberFormValues(values, {
                    isPayerIdActive: claimEditorStore.isPayerIdActive,
                })
            }
            onSubmit={() => {}}
            enableReinitialize
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={false}
        >
            {formik => {
                // Auto-save on value change - save whenever values change, not just when dirty
                useEffect(() => {
                    debouncedSave();
                }, [formik.values, debouncedSave]);

                useClaimEditorRevealFieldErrors(formik, validationSignal, validationEvaluated, () => {
                    debouncedSave.cancel();
                    persistSubscriber(formik.values);
                });

                return (
                <S.FormContainer>
                    <S.FormGrid>
                        <S.FormField span={2}>
                            <S.Label>Lookup Subscriber</S.Label>
                            <Field
                                name="subscriberLookup"
                                component={AsyncSelectField}
                                placeholder="Type name to search"
                                loadOptions={async (q: string) => {
                                    const results: SupportSubscriber[] = await claimEditorStore.searchSubscribersByName(q);
                                    return (results || []).map(r => ({
                                        label: [r.first, r.middle, r.last].filter(Boolean).join(' '),
                                        value: r.id as any,
                                        subscriber: r,
                                        info: formatLookupInfo(r.dob, r.sex ? _.startCase(_.lowerCase(r.sex)) : undefined),
                                    } as any));
                                }}
                                onChange={(opt: any, form: any) => {
                                    const sub = opt?.subscriber;
                                    if (!sub) {
                                        form.setFieldValue('subscriberId', undefined);
                                        return;
                                    }
                                    const addr = sub?.addresses?.[0];
                                    // Demographics only. payerId, memberId and the patient
                                    // relationship describe the POLICY, not the person, so a
                                    // name lookup must never re-key which payer this subscriber
                                    // is billed under. Carrying them forward through `prev`
                                    // rather than re-listing them also keeps this immune to a
                                    // stale form bag.
                                    form.setValues((prev: SubscriberPayerFormValues) => ({
                                        ...prev,
                                        subscriberLookup: opt.label,
                                        subscriberId: sub?.id || '',
                                        ssn: sub?.ssn || '',
                                        first: sub?.first || '',
                                        middle: sub?.middle || '',
                                        last: sub?.last || '',
                                        suffix: sub?.suffix || '',
                                        dob: sub?.dob || '',
                                        sex: (sub?.sex as any) || '',
                                        phone: sub?.phone || '',
                                        address1: addr?.lines?.[0] || '',
                                        address2: addr?.lines?.[1] || '',
                                        city: addr?.city || '',
                                        state: (addr?.state as any) || '',
                                        country: (addr?.country as any) || '',
                                        postal: addr?.postal || '',
                                    }));
                                    debouncedSave();
                                }}
                            />
                        </S.FormField>

                        <S.FormField span={2} id={fieldDomId(`${fieldPrefix}.payerId`)}>
                            <S.Label required>Lookup Payer</S.Label>
                            <Field
                                name="payerLookup"
                                component={AsyncSelectField}
                                key={claimEditorStore.arePayersLoaded ? 'payers-ready' : 'payers-loading'}
                                cacheOptions={claimEditorStore.arePayersLoaded}
                                loadOptions={async (q: string) => {
                                    if (!claimEditorStore.arePayersLoaded) {
                                        await claimEditorStore.preloadPayers();
                                    }

                                    const list: ProviderPayer[] = (claimEditorStore.payers as ProviderPayer[]) || [];

                                    // Filtering AND ranking live in services/payer-search so they
                                    // can be unit tested — this used to be an untestable closure.
                                    //
                                    // Ranking matters as much as the cap: the list arrives NAME
                                    // ASCENDING, so truncating it alphabetically hid the payer
                                    // literally called "Medicare Part B" behind fifty payers whose
                                    // names merely contain the word (FE-34).
                                    const { matches, hidden } = searchPayers(list, q || '', MAX_PAYER_OPTIONS);

                                    const options = matches.map(
                                        p =>
                                            ({
                                                label: p.name || '',
                                                value: p.id as any,
                                                payer: p,
                                                info: p.coverage ? '| ' + p.coverage : undefined,
                                            } as any),
                                    );

                                    // A truncated list must SAY it is truncated. Capped at 10 with
                                    // no notice, "blue" returned ten BCBS plans ending at Florida
                                    // and every plan after F looked as though it did not exist —
                                    // on a claims console that is a mis-routed claim, not a
                                    // cosmetic issue.
                                    if (hidden > 0) {
                                        options.push({
                                            label: `+${hidden} more — keep typing to narrow`,
                                            value: '__truncated__',
                                            isDisabled: true,
                                        } as any);
                                    }

                                    return options;
                                }}
                                getDefault={async (id: string) => {
                                    if (!id) return null;
                                    try {
                                        const payer = await claimEditorStore.getPayerById(id);
                                        if (payer && claimEditorStore.isPayerIdActive(id)) {
                                            return {
                                                label: payer.name || '',
                                                value: payer.id as any,
                                                payer,
                                            } as any;
                                        }
                                        return {
                                            label: payer?.name
                                                ? `${payer.name} (disabled)`
                                                : DISABLED_PAYER_MESSAGE,
                                            value: id,
                                        } as any;
                                    } catch (e) {
                                        console.warn('payer getDefault failed', e);
                                    }
                                    // Keep the select interactive when the current payer
                                    // cannot be resolved (e.g. disabled on provider).
                                    return {
                                        label: DISABLED_PAYER_MESSAGE,
                                        value: id,
                                    } as any;
                                }}
                                onChange={(opt: any, form: any) => {
                                    const payer = opt?.payer;
                                    if (!payer) {
                                        // Clearing the select has to clear the id as well.
                                        // Bailing out here left `payerId` populated, so the
                                        // field read as empty while the submitted claim still
                                        // carried the previous payer. AsyncSelectField clears
                                        // `payerLookup` itself.
                                        form.setFieldValue('payerId', '');
                                        debouncedSave();
                                        return;
                                    }
                                    form.setFieldValue('payerLookup', String(payer.id || ''));
                                    form.setFieldValue('payerId', payer.id);
                                    // Avoid full setValues replacement here; it can clobber in-progress edits.
                                    debouncedSave();
                                }}
                            />
                        </S.FormField>

                        <S.FormField id={fieldDomId(`${fieldPrefix}.first`)}>
                            <S.Label required>Subscriber First Name</S.Label>
                            <Field name="first" placeholder="Subscriber First Name" component={InputField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Subscriber Middle Name</S.Label>
                            <Field name="middle" placeholder="Subscriber Middle Name" component={InputField} />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.last`)}>
                            <S.Label required>Subscriber Last Name</S.Label>
                            <Field name="last" placeholder="Subscriber Last Name" component={InputField} />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.suffix`)}>
                            <S.Label>Suffix</S.Label>
                            <Field name="suffix" placeholder="Suffix" component={InputField} maxLength={10 as any} />
                        </S.FormField>

                        <S.FormField id={fieldDomId(`${fieldPrefix}.dob`)}>
                            <S.Label required>Subscriber Date of Birth</S.Label>
                            <Field name="dob" placeholder="YYYY-MM-DD" component={DatePickerField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Subscriber Sex</S.Label>
                            <Field
                                name="sex"
                                component={SelectField}
                                options={sexOptions as any}
                                placeholder="Select"
                                isClearable={true}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Patient Relationship to Subscriber</S.Label>
                            <Field
                                name="relationshipToPatient"
                                component={SelectField}
                                options={relationshipOptions as any}
                                placeholder="Select"
                                isClearable={true}
                            />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.memberId`)}>
                            <S.Label required>Member ID</S.Label>
                            <Field name="memberId" placeholder="Member ID" component={InputField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>SSN</S.Label>
                            <Field name="ssn" placeholder="Social security number" component={InputField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Phone Number</S.Label>
                            <Field name="phone" placeholder="Phone Number" component={InputField} />
                        </S.FormField>

                        <S.FormField span={2} id={fieldDomId(`${fieldPrefix}.addresses[0].lines[0]`)}>
                            <S.Label required>Address Line 1</S.Label>
                            <Field name="address1" placeholder="Address Line 1" component={InputField} />
                        </S.FormField>
                        <S.FormField span={2}>
                            <S.Label>Address Line 2</S.Label>
                            <Field name="address2" placeholder="Address Line 2" component={InputField} />
                        </S.FormField>

                        <S.FormField id={fieldDomId(`${fieldPrefix}.addresses[0].city`)}>
                            <S.Label required>City</S.Label>
                            <Field name="city" placeholder="City" component={InputField} />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.addresses[0].state`)}>
                            <S.Label required>State</S.Label>
                            <Field
                                name="state"
                                component={SelectField}
                                options={stateOptions as any}
                                placeholder="Select State"
                                isClearable={false}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Country</S.Label>
                            <Field
                                name="country"
                                component={SelectField}
                                options={countryOptions as any}
                                placeholder="Select Country"
                                isClearable={true}
                            />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.addresses[0].postal`)}>
                            <S.Label required>Postal Code</S.Label>
                            <Field name="postal" placeholder="Postal Code" component={InputField} />
                        </S.FormField>
                    </S.FormGrid>
                </S.FormContainer>
                );
            }}
        </Formik>
    );
});

export default SubscriberPayerForm;


